// frontend/src/pages/itemSetupFields.js
//
// The Item setup grid's save-routing contract: which edited field goes to
// which endpoint, and which edits leave the PO recommendations stale.
// The counterpart of itemSetupRow.js (wire → grid): this is grid → wire.
// Pinned by itemSetupFields.test.js without rendering the grid.

// Fields that belong to the Item model (saves go to /api/items/bulk-update/).
// ``user`` / ``product_category``
// are nested objects on the wire — the change-tracker carries them to the
// payload-building loop so it can extract the FK PK.
export const ITEM_FIELDS = new Set([
  'shopify_sku', 'item_name', 'item_type', 'brand', 'barcode',
  'current_sell_price', 'compare_at_price', 'current_cost_price_supplier_ccy',
  'weight', 'length', 'width', 'height',
  'inventory_tracked', 'allows_overselling', 'requires_shipping',
  'item_status', 'npi_flag', 'user', 'product_category',
  // The default supplier is NOT here: it is read-only in the grid and written
  // by the per-item Suppliers dialog (immediately, outside the save batch).
]);

// Terms of the item's DEFAULT supplier relation (saves go to
// /api/supplier-catalog-items/bulk-update/, addressed by the row's
// ``default_supplier_link_id`` — or by every ``supplier_link_ids`` entry,
// see defaultLinkBatch). One edit per item, so these are item-scoped like
// ITEM_FIELDS. The names are the relation's own wire fields.
export const DEFAULT_LINK_FIELDS = new Set(['supplier_sku', 'order_multiple']);

/** True for a field with one value per item — an edit shows on, and saves
 * once from, every location row of that item. */
export const isItemScoped = (field) => ITEM_FIELDS.has(field) || DEFAULT_LINK_FIELDS.has(field);

/**
 * Where an edit of `field` on `row` is saved: the batch (`item`,
 * `defaultLink`, `itemLocation`) and the id its payload entry and the
 * server's per-row result carry. Cells with the same target and id are one
 * write.
 */
export function saveTargetOf(field, row) {
  if (ITEM_FIELDS.has(field)) return { target: 'item', id: row._ItemID };
  if (DEFAULT_LINK_FIELDS.has(field)) return { target: 'defaultLink', id: row.default_supplier_link_id };
  return { target: 'itemLocation', id: row.item_location_id };
}

/**
 * The default-supplier batch. Each edited item's fields go to its default
 * relation only (`scope` 'default') or to every relation of the item
 * ('all' — the row's `supplier_link_ids`).
 *
 * @param {Map} buckets  default_supplier_link_id → { rowData, fields: Set }
 * @param {'default'|'all'} scope
 * @returns {{ items: object[], savedIds: (results: object[]) => Array }}
 *   the bulk payload, and a reader turning the server's per-relation results
 *   into the default-link ids whose edit fully saved: an item counts only
 *   when every relation it was sent to saved.
 */
export function defaultLinkBatch(buckets, scope) {
  if (scope !== 'default' && scope !== 'all') {
    throw new Error(`defaultLinkBatch: unknown scope "${scope}"`);
  }
  const sentTo = new Map();
  const items = [];
  for (const [defaultId, { rowData, fields }] of buckets) {
    const payloadFields = Object.fromEntries([...fields].map((f) => [f, rowData[f]]));
    const ids = scope === 'all' ? rowData.supplier_link_ids : [defaultId];
    sentTo.set(defaultId, ids);
    for (const id of ids) items.push({ id, fields: payloadFields });
  }
  const savedIds = (results) => {
    const saved = new Set(results.filter((r) => r.success).map((r) => String(r.id)));
    return [...sentTo]
      .filter(([, ids]) => ids.every((id) => saved.has(String(id))))
      .map(([defaultId]) => defaultId);
  };
  return { items, savedIds };
}

// Every other editable field belongs to the ItemLocation model (saves go to
// /api/item-locations/bulk-update/). Min/max order qty are per-relation
// (SupplierCatalogItem), edited on the Supplier catalog screen.
// ``inventory_target_mode`` / ``target_days_of_supply`` are nullable overrides
// (null = inherit) — the bulk payload carries the null (itemPlanningColumns.js).

// Fields that affect PO recommendation calculations — changing one makes the
// existing recommendations stale, which the save's success message says.
export const PO_STALE_FIELDS = new Set([
  'service_level_pct', 'safety_stock_units',
  'min_inventory_units', 'inventory_target_mode', 'target_days_of_supply',
  'order_multiple',
]);
